// The bank's audit trail (RFC-056 Invariants: every change to the bank is
// an audit row naming who made it): newest first, each row who, what, to
// what and when, narrowed to one target by typing its id. As rows
// (byj-1wv.51.18): the actor's mark — a job's own for what byj did — and
// the search over them as a list's toolbar.
import { useCallback, useEffect, useState } from "react";
import type { AuditRow, BankClient } from "../bank";
import { t, whenMinute, word } from "../i18n";
import { EmptyRows, initialOf } from "./Rows";
import { paperOf, targetName, type BankNames } from "./BankItems";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** An action's words, its key itself when the app has none for it yet. */
export const actionWords = (action: string) => word("bank:action", action) ?? action;

/** How many rows of the trail are shown at a time. */
const auditPage = 10;

export function BankAudit({ client, names: titles }: { client: BankClient; names: BankNames }) {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  // the newest first, a page at a time: the trail sits under the jobs, not over them
  const [shown, setShown] = useState(auditPage);
  const load = useCallback(
    async (target: string) => {
      try {
        setRows(await client.audit({ target: target.trim() }));
        setShown(auditPage);
        setError(null);
      } catch (err) {
        setError(errText(err));
      }
    },
    [client],
  );
  useEffect(() => {
    void load("");
  }, [load]);
  return (
    <div data-testid="bank-audit">
      <p className="muted small panel-lead">{t("bank:auditLead")}</p>
      <form
        className="row wrap panel-tools bank-audit-find"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          void load(target);
        }}
      >
        <input type="search" value={target} onChange={(e) => setTarget(e.target.value)} placeholder={t("bank:auditTarget")} aria-label={t("bank:auditTarget")} />
        <button type="submit">{t("bank:auditFind")}</button>
      </form>
      <p className="muted small bank-find-hint">{t("bank:auditFindHint")}</p>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {rows === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : rows.length === 0 ? (
        <EmptyRows text={t("bank:auditNone")} />
      ) : (
        <>
        <ul className="rows">
          {rows.slice(0, shown).map((a) => {
            const job = a.actor.startsWith("job:");
            const name = a.target ? targetName(a.target, titles, paperOf(a)) : null;
            return (
              <li key={a.id} className="share-row no-menu is-live" data-testid="audit-row">
                <span className="row-lead" />
                <span className="kind-box" aria-hidden="true">
                  {job ? "job" : initialOf(a.actor_email || a.actor)}
                </span>
                <div className="row-main">
                  <span className="row-title">{actionWords(a.action)}</span>
                  <div className="row-sub">
                    {job ? t("bank:auditByJob") : a.actor_email || a.actor}
                    {/* what it was done to by its name, its id kept small beside it as a job's row keeps it */}
                    {a.target && (
                      <>
                        {" · "}
                        {name && `${name} `}
                        <code className="bank-target">{a.target}</code>
                      </>
                    )}
                  </div>
                </div>
                <div className="row-meta">
                  <span className="muted small">{whenMinute(a.at)}</span>
                </div>
              </li>
            );
          })}
        </ul>
        {rows.length > shown && (
          <button className="small bank-show-more" data-testid="bank-audit-more" onClick={() => setShown((n) => n + auditPage)}>
            {t("bank:auditMore", { n: Math.min(auditPage, rows.length - shown), left: rows.length - shown })}
          </button>
        )}
        </>
      )}
    </div>
  );
}
